import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { api } from "@src/services/api/client";
import type { Livestream } from "@src/services/api/cameras-api";
import { getResolvedApiBaseUrl } from "@src/services/api/request";
import { useTenantId } from "@src/features/tenants";
import { useCachingConfig } from "@src/config/caching-config";

export type UseLivestreamOptions = {
  siteId?: string;
  cameraKey: string;
};

const preferredFormats: Livestream["format"][] = ["mjpg", "mjpeg", "http-mjpeg", "http-mp4"];

function selectLivestream(livestreams?: Livestream[]) {
  if (!livestreams?.length) return undefined;
  for (const format of preferredFormats) {
    const match = livestreams.find((ls) => ls.format === format);
    if (match) return match;
  }
  return livestreams[0];
}

/**
 * Hook to manage livestream sessions for a camera.
 * Fetches camera details to get livestream capabilities, then derives the stream URL synchronously.
 */
export function useLivestream({ siteId, cameraKey }: UseLivestreamOptions) {
  const tenantId = useTenantId();
  const cachingConfig = useCachingConfig();
  if (!siteId) {
    console.error("useLivestream: siteId is required");
  }
  const effectiveSiteId = siteId || "";

  // Fetch camera — reuse cache from the detail screen
  const cameraQuery = useQuery({
    queryKey: ["cameras", tenantId, effectiveSiteId, cameraKey],
    queryFn: () => api.cameras.get(tenantId, effectiveSiteId, cameraKey),
    enabled: !!tenantId && !!cameraKey,
    staleTime: cachingConfig.cameras.snapTtlSec * 1000,
    placeholderData: keepPreviousData,
  });

  const selectedLivestream = selectLivestream(cameraQuery.data?.livestreams);

  // Resolve API base URL for relative session paths (async, cached forever)
  const apiBaseQuery = useQuery({
    queryKey: ["api-base-url"],
    queryFn: getResolvedApiBaseUrl,
    staleTime: Infinity,
  });

  // Derive stream URL synchronously — no mutation, no extra render cycle
  const streamUrl = useMemo(() => {
    if (!selectedLivestream) return undefined;
    const start = selectedLivestream.session.start;
    if (start.startsWith("http")) return start;
    if (apiBaseQuery.data) return `${apiBaseQuery.data}${start}`;
    return undefined;
  }, [selectedLivestream, apiBaseQuery.data]);

  return {
    // Camera data
    camera: cameraQuery.data,
    isCameraLoading: cameraQuery.isLoading,
    cameraError: cameraQuery.error,
    refetchCamera: cameraQuery.refetch,

    // Livestream capability
    livestream: selectedLivestream,
    hasLivestream: !!selectedLivestream,
    streamFormat: selectedLivestream?.format,

    // Stream URL (derived synchronously)
    streamUrl,
  };
}
